// components/SpecScanner.jsx — read a project manual for the drywall trade.
//   • Upload the spec PDF: it's read in the browser (no AI, no upload of the PDF) → CSI sections → articles.
//   • Drywall sections are picked out (09 21 16, 09 22 16, 05 40 00, 07 21 00, 07 84 00, 09 51 00 …) and
//     sections that mention our work (blocking, "by Section 09 21 16" …).
//   • Search the spec instantly (trade-aware: 5/8 = .625, 20ga = 33 mil, "level 5", "deflection track" …).
//   • Summaries (AI, cached — each section costs once), a drywall-scope overview, and "Ask the spec": answers from
//     the few best-matching articles only, with the last few messages as context — lean on purpose.
//   • Anything worth asking the architect → "+ RFI".
//
// Props: projectId | opportunityId, ownerLabel, onAddToRfi(item) (optional), onBack (optional)

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist";
import {
  readSpecPages,
  splitSpec,
  specMeta,
  specChecks,
  buildSpecIndex,
  searchSpec,
} from "../lib/takeoff/specParse";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const API = "https://erp-xe9u.onrender.com/api";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "X-Tenant-ID": localStorage.getItem("tenantId"),
});
const cls = (...c) => c.filter(Boolean).join(" ");
const errText = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.error ||
  e?.message ||
  "Something went wrong";
const fmtK = (n) =>
  n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n || 0);

const TRADE = {
  core: { label: "Drywall", cls: "bg-orange-500/15 text-orange-300" },
  related: { label: "Related", cls: "bg-sky-500/15 text-sky-300" },
  mentions: { label: "Mentions", cls: "bg-violet-500/15 text-violet-300" },
};
const CHIPS = [
  ["Board types", "gypsum board type"],
  ["Framing & gauge", "stud gauge"],
  ["Finish levels", "level finish"],
  ["Deflection / head", "deflection track"],
  ["Fire-rated", "fire rated assemblies"],
  ["STC / acoustic", "STC sound"],
  ["Insulation", "insulation"],
  ["Ceilings", "ceiling suspension"],
  ["Firestopping", "firestop"],
  ["Blocking", "blocking reinforcing"],
  ["Mock-ups", "mock-up"],
  ["Engineering", "delegated design engineer"],
];
const STARTERS = [
  "What gypsum board types are specified, and where?",
  "What stud size, gauge and spacing are required?",
  "What finish levels are required, and where?",
  "What are the deflection / head-of-wall requirements?",
  "What insulation and acoustic sealant is required?",
  "What is by others or in another section?",
];

// "- bullet" lines + **bold** → JSX
function MiniMd({ text, className }) {
  const lines = String(text || "")
    .split(/\n+/)
    .filter((l) => l.trim());
  const bold = (s) =>
    s.split(/(\*\*[^*]+\*\*|\[[^\]]{1,40}\])/g).map((p, i) =>
      p.startsWith("**") ? (
        <b key={i} className="text-white">
          {p.slice(2, -2)}
        </b>
      ) : /^\[[^\]]+\]$/.test(p) ? (
        <span
          key={i}
          className="whitespace-nowrap rounded bg-white/5 px-1 font-mono text-[10.5px] text-gray-400"
        >
          {p.slice(1, -1)}
        </span>
      ) : (
        p
      ),
    );
  return (
    <div className={cls("space-y-1", className)}>
      {lines.map((l, i) => {
        const m = l.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)$/);
        return m ? (
          <div key={i} className="flex gap-2">
            <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-orange-400" />
            <span>{bold(m[1])}</span>
          </div>
        ) : (
          <p key={i}>{bold(l)}</p>
        );
      })}
    </div>
  );
}

function Highlight({ text, q }) {
  const words = String(q || "")
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length) return <>{text}</>;
  const re = new RegExp(`(${words.join("|")})`, "gi");
  return (
    <>
      {String(text)
        .split(re)
        .map((p, i) =>
          i % 2 === 1 ? (
            <mark
              key={i}
              className="rounded bg-orange-500/30 px-0.5 text-orange-100"
            >
              {p}
            </mark>
          ) : (
            p
          ),
        )}
    </>
  );
}

export default function SpecScanner({
  projectId,
  opportunityId,
  ownerLabel = "",
  onAddToRfi,
  onBack,
}) {
  const ownerQ = projectId ? { projectId } : { opportunityId };
  const [specs, setSpecs] = useState([]);
  const [specId, setSpecId] = useState(null);
  const [spec, setSpec] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [aiInfo, setAiInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [prog, setProg] = useState(null); // { stage, done, total }
  const [secFilter, setSecFilter] = useState("ours"); // ours | all
  const [secQ, setSecQ] = useState("");
  const [view, setView] = useState("overview"); // overview | section | search
  const [selNum, setSelNum] = useState(null);
  const [focusKey, setFocusKey] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("ours");
  const [busy, setBusy] = useState(new Set()); // section nums being summarized / "overview"
  const [batch, setBatch] = useState(null); // { done, total }
  const [messages, setMessages] = useState([]);
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [added, setAdded] = useState(new Set());
  const fileRef = useRef(null);
  const chatEnd = useRef(null);
  const readerRef = useRef(null);

  const get = (url, params) =>
    axios
      .get(`${API}${url}`, { headers: authHeaders(), params })
      .then((r) => r.data);
  const post = (url, body) =>
    axios
      .post(`${API}${url}`, body, { headers: authHeaders() })
      .then((r) => r.data);

  // ── load ──
  const loadList = async (pick) => {
    try {
      const d = await get("/specs", ownerQ);
      setSpecs(d.specs || []);
      setAiInfo(d.ai || null);
      const id =
        pick ||
        specId ||
        d.specs?.find((x) => x.status === "ready")?._id ||
        null;
      if (id && id !== specId) setSpecId(id);
    } catch (e) {
      setErr(errText(e));
    }
  };
  useEffect(() => {
    loadList();
  }, [projectId, opportunityId]); // eslint-disable-line
  useEffect(() => {
    if (!specId) return;
    let dead = false;
    setLoading(true);
    setErr("");
    Promise.all([
      get(`/specs/${specId}`),
      get(`/specs/${specId}/chunks`),
      get(`/specs/${specId}/chat`),
    ])
      .then(([a, b, c]) => {
        if (dead) return;
        setSpec(a.spec);
        setAiInfo(a.ai || null);
        setChunks(b.chunks || []);
        setMessages(c.messages || []);
        setView("overview");
        setSelNum(null);
      })
      .catch((e) => !dead && setErr(errText(e)))
      .finally(() => !dead && setLoading(false));
    return () => {
      dead = true;
    };
  }, [specId]); // eslint-disable-line

  const sections = spec?.sections || [];
  const index = useMemo(
    () => buildSpecIndex(chunks, sections),
    [chunks, spec?._id],
  ); // eslint-disable-line
  const ours = useMemo(
    () => new Set(sections.filter((s) => s.trade).map((s) => s.num)),
    [sections],
  );
  const core = sections.filter((s) => s.trade === "core");
  const results = useMemo(
    () =>
      q.trim()
        ? searchSpec(index, q, {
            sections: scope === "ours" && ours.size ? ours : null,
            limit: 60,
          })
        : [],
    [index, q, scope, ours],
  );
  const sel = sections.find((s) => s.num === selNum) || null;
  const selChunks = useMemo(
    () => chunks.filter((c) => c.section === selNum),
    [chunks, selNum],
  );
  const shownSections = sections.filter(
    (s) =>
      (secFilter === "all" || s.trade) &&
      (!secQ.trim() ||
        `${s.num} ${s.title}`
          .toLowerCase()
          .includes(secQ.trim().toLowerCase())),
  );

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ block: "end" });
  }, [messages, asking]);
  useEffect(() => {
    if (!focusKey) return;
    const t = setTimeout(() => {
      readerRef.current
        ?.querySelector(`[data-key="${CSS.escape(focusKey)}"]`)
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [focusKey, selNum]);

  const openSection = (num, key = null) => {
    setSelNum(num);
    setFocusKey(key);
    setView("section");
  };

  // ── upload: read the PDF here, send sections + text ──
  const upload = async (file) => {
    if (!file) return;
    setErr("");
    setNote("");
    setProg({ stage: "Reading the PDF", done: 0, total: 1 });
    try {
      const pdf = await pdfjsLib.getDocument({
        data: new Uint8Array(await file.arrayBuffer()),
      }).promise;
      const pages = await readSpecPages(pdf, (done, total) =>
        setProg({ stage: "Reading pages", done, total }),
      );
      const { sections: secs, chunks: chs } = splitSpec(pages);
      const meta = specMeta(pages);
      const checks = specChecks(secs, chs);
      pdf.destroy();
      if (!chs.length)
        throw new Error(
          "No text found in this PDF — is it a scanned image? (Scanned specs need OCR first.)",
        );
      setProg({ stage: "Saving", done: 0, total: chs.length });
      const { spec: created } = await post("/specs", {
        ...ownerQ,
        name: meta.title || file.name.replace(/\.pdf$/i, ""),
        fileName: file.name,
        pageCount: pages.length,
        meta,
        sections: secs,
        checks,
      });
      // text in batches under ~80 KB each — fits the default 100 KB body limit of a global express.json()
      let i = 0;
      while (i < chs.length) {
        const part = [];
        let size = 0;
        while (
          i < chs.length &&
          part.length < 120 &&
          (size < 80000 || !part.length)
        ) {
          part.push(chs[i]);
          size += new Blob([chs[i].text]).size + 200;
          i++;
        }
        await post(`/specs/${created._id}/chunks`, { chunks: part });
        setProg({ stage: "Saving", done: i, total: chs.length });
      }
      await post(`/specs/${created._id}/ready`, {});
      setProg(null);
      setNote(
        `Read ${pages.length} pages · ${secs.length} sections · ${secs.filter((s) => s.trade === "core").length} drywall sections`,
      );
      await loadList(created._id);
    } catch (e) {
      setProg(null);
      setErr(errText(e));
    }
  };

  // ── AI ──
  const refreshUsage = async () => {
    try {
      const d = await get(`/specs/${specId}`);
      setSpec((p) => (p ? { ...p, usage: d.spec.usage } : p));
    } catch {
      /* the numbers update next time */
    }
  };
  const summarize = async (num, force = false) => {
    setBusy((b) => new Set(b).add(num));
    setErr("");
    try {
      const d = await post(
        `/specs/${specId}/sections/${encodeURIComponent(num.replace(/ /g, "_"))}/summarize${force ? "?force=1" : ""}`,
        {},
      );
      setSpec((p) => ({
        ...p,
        sections: p.sections.map((s) =>
          s.num === num ? { ...s, summary: d.section.summary } : s,
        ),
      }));
      if (!d.cached) refreshUsage();
      return true;
    } catch (e) {
      setErr(errText(e));
      return false;
    } finally {
      setBusy((b) => {
        const n = new Set(b);
        n.delete(num);
        return n;
      });
    }
  };
  const summarizeAll = async () => {
    const todo = sections.filter(
      (s) => (s.trade === "core" || s.trade === "related") && !s.summary?.at,
    );
    setBatch({ done: 0, total: todo.length });
    for (let k = 0; k < todo.length; k++) {
      const ok = await summarize(todo[k].num);
      if (!ok) break;
      setBatch({ done: k + 1, total: todo.length });
    }
    setBatch(null);
  };
  const buildOverview = async () => {
    setBusy((b) => new Set(b).add("overview"));
    setErr("");
    try {
      const d = await post(`/specs/${specId}/overview`, {});
      setSpec((p) => ({ ...p, overview: d.overview }));
      refreshUsage();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy((b) => {
        const n = new Set(b);
        n.delete("overview");
        return n;
      });
    }
  };
  // the articles a question is answered from: picked here (no AI) — drywall sections first
  const pickKeys = (question) => {
    const lastUser =
      [...messages].reverse().find((m) => m.role === "user")?.text || "";
    const opt = { sections: ours.size ? ours : null, limit: 8, loose: true };
    // the question itself first; a short follow-up ("and at openings?") also borrows the previous question
    let hits = searchSpec(index, question, opt);
    if (question.length < 45 && lastUser)
      hits = [
        ...hits.slice(0, 4),
        ...searchSpec(index, `${question} ${lastUser}`, opt),
      ];
    if (hits.length < 3)
      hits = [
        ...hits,
        ...searchSpec(index, question, { ...opt, sections: null }),
      ];
    const seen = new Set();
    return hits
      .filter((h) => !seen.has(h.chunk.key) && seen.add(h.chunk.key))
      .slice(0, 7)
      .map((h) => h.chunk.key);
  };
  const send = async (text) => {
    const question = String(text ?? ask).trim();
    if (!question || asking) return;
    setAsk("");
    setAsking(true);
    setErr("");
    const mine = { role: "user", text: question, at: new Date().toISOString() };
    setMessages((m) => [...m, mine]);
    try {
      const d = await post(`/specs/${specId}/ask`, {
        question,
        keys: pickKeys(question),
      });
      setMessages((m) => [...m, d.message]);
      setSpec((p) =>
        p
          ? {
              ...p,
              usage: {
                ...p.usage,
                calls: (p.usage?.calls || 0) + 1,
                inTok: (p.usage?.inTok || 0) + (d.usage?.inTok || 0),
                outTok: (p.usage?.outTok || 0) + (d.usage?.outTok || 0),
              },
            }
          : p,
      );
    } catch (e) {
      setMessages((m) => m.filter((x) => x !== mine));
      setAsk(question);
      setErr(errText(e));
    } finally {
      setAsking(false);
    }
  };
  const clearChat = async () => {
    if (!window.confirm("Clear this conversation?")) return;
    await axios
      .delete(`${API}/specs/${specId}/chat`, { headers: authHeaders() })
      .catch(() => {});
    setMessages([]);
  };
  const deleteSpec = async () => {
    if (
      !spec ||
      !window.confirm(
        `Delete “${spec.name}” and its summaries and conversations?`,
      )
    )
      return;
    await axios
      .delete(`${API}/specs/${spec._id}`, { headers: authHeaders() })
      .catch((e) => setErr(errText(e)));
    setSpec(null);
    setSpecId(null);
    setChunks([]);
    loadList();
  };

  const toRfi = (idea, key) => {
    if (!onAddToRfi) return;
    onAddToRfi({
      question: idea.question,
      assumption: "",
      source: {
        kind: "spec",
        label: idea.ref
          ? `Spec ${/^\d{2} \d{2}/.test(idea.ref) ? idea.ref : `${idea.section || ""} §${idea.ref}`}`.replace(
              /\s+/g,
              " ",
            )
          : `Spec ${idea.section || ""}`,
        page: idea.page || null,
        specId: spec?._id,
        section: idea.section || "",
        key,
      },
      why: idea.why,
    });
    setAdded((s) => new Set(s).add(key));
    setNote("Added to the RFI list");
  };
  const RfiIdea = ({ idea, k }) => (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] text-gray-100">{idea.question}</div>
        <div className="mt-0.5 text-[10.5px] text-gray-500">
          {idea.ref ? <span className="font-mono">{idea.ref}</span> : null}
          {idea.page ? ` · p.${idea.page}` : ""}
          {idea.why ? ` · ${idea.why}` : ""}
        </div>
      </div>
      {onAddToRfi && (
        <button
          disabled={added.has(k)}
          onClick={() => toRfi(idea, k)}
          className="shrink-0 rounded-md bg-amber-500/15 px-2 py-1 text-[11px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
        >
          {added.has(k) ? "✓ Added" : "+ RFI"}
        </button>
      )}
    </div>
  );

  const usage = spec?.usage || {};
  const cost =
    aiInfo?.in && aiInfo?.out
      ? ((usage.inTok || 0) * aiInfo.in + (usage.outTok || 0) * aiInfo.out) /
        1e6
      : null;
  const pending = sections.filter(
    (s) => (s.trade === "core" || s.trade === "related") && !s.summary?.at,
  ).length;

  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-950 text-gray-100">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-gray-900/80 px-4 py-3">
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-white/10"
          >
            ← Back
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-white">Spec scanner</h1>
          {ownerLabel && (
            <div className="truncate text-xs text-gray-500">{ownerLabel}</div>
          )}
        </div>
        <select
          value={specId || ""}
          onChange={(e) => setSpecId(e.target.value || null)}
          className="ml-2 max-w-[22rem] rounded-lg border border-gray-700 bg-gray-950 px-2 py-1.5 text-xs text-gray-200"
        >
          {!specs.length && <option value="">No specs yet</option>}
          {specs.map((s) => (
            <option key={s._id} value={s._id}>
              {s.name} · {s.pageCount} pages
              {s.status !== "ready" ? " (incomplete)" : ""}
            </option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) =>
            upload(e.target.files?.[0]).finally(() => (e.target.value = ""))
          }
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={!!prog}
          className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-50"
        >
          + Upload spec PDF
        </button>
        {spec && (
          <button
            onClick={deleteSpec}
            className="rounded-lg px-2 py-1.5 text-xs text-gray-500 hover:bg-red-500/10 hover:text-red-300"
          >
            Delete
          </button>
        )}
        <div className="ml-auto flex items-center gap-3 text-[11px] text-gray-500">
          {spec && (
            <span title={aiInfo?.model ? `Model: ${aiInfo.model}` : ""}>
              AI: {usage.calls || 0} calls ·{" "}
              {fmtK((usage.inTok || 0) + (usage.outTok || 0))} tokens
              {cost != null
                ? ` · ≈$${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(2)}`
                : ""}
            </span>
          )}
          {aiInfo && !aiInfo.configured && (
            <span className="text-amber-300">
              AI not configured — search works, summaries don't
            </span>
          )}
          <button
            onClick={() => setChatOpen((v) => !v)}
            className="rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-gray-300 hover:bg-white/10"
          >
            {chatOpen ? "Hide chat" : "Ask the spec"}
          </button>
        </div>
      </div>
      {(err || note || prog) && (
        <div className="flex items-center gap-3 border-b border-white/10 px-4 py-2 text-xs">
          {prog && (
            <>
              <span className="text-gray-300">
                {prog.stage}…{" "}
                {prog.total > 1 ? `${prog.done} / ${prog.total}` : ""}
              </span>
              <div className="h-1.5 w-60 overflow-hidden rounded-full bg-gray-800">
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{
                    width: `${prog.total ? (prog.done / prog.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </>
          )}
          {err && <span className="text-red-300">{err}</span>}
          {!err && note && <span className="text-emerald-300">{note}</span>}
        </div>
      )}

      {!spec && !loading ? (
        <div className="grid flex-1 place-items-center p-8 text-center">
          <div className="max-w-md">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-orange-500/10 text-xl text-orange-300">
              §
            </div>
            <p className="text-sm font-medium text-gray-200">
              Upload the project manual (spec book)
            </p>
            <p className="mt-1 text-xs text-gray-500">
              It's read right here in your browser. The drywall sections are
              picked out, you can search the whole book instantly, get short
              summaries of what affects the price, ask questions, and turn
              anything unclear into an RFI.
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-4 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400"
            >
              Upload spec PDF
            </button>
          </div>
        </div>
      ) : (
        <div
          className={cls(
            "grid min-h-0 flex-1 grid-cols-1",
            chatOpen
              ? "lg:grid-cols-[270px_1fr_380px]"
              : "lg:grid-cols-[270px_1fr]",
          )}
        >
          {/* sections */}
          <aside className="flex min-h-0 flex-col border-r border-white/10">
            <div className="space-y-2 border-b border-white/10 p-3">
              <button
                onClick={() => setView("overview")}
                className={cls(
                  "w-full rounded-lg px-3 py-2 text-left text-[13px] font-semibold",
                  view === "overview"
                    ? "bg-orange-500/15 text-orange-200"
                    : "bg-white/5 text-gray-200 hover:bg-white/10",
                )}
              >
                Drywall scope overview
              </button>
              <input
                value={secQ}
                onChange={(e) => setSecQ(e.target.value)}
                placeholder="Filter sections…"
                className="w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-xs text-gray-100 focus:border-orange-500 focus:outline-none"
              />
              <div className="inline-flex rounded-lg bg-gray-900 p-0.5 text-[11px]">
                {[
                  ["ours", "Drywall-related"],
                  ["all", `All (${sections.length})`],
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setSecFilter(k)}
                    className={cls(
                      "rounded-md px-2.5 py-1 font-medium",
                      secFilter === k
                        ? "bg-orange-500 text-white"
                        : "text-gray-400 hover:text-gray-200",
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading && <p className="p-4 text-xs text-gray-500">Loading…</p>}
              {shownSections.map((s) => (
                <button
                  key={s.num}
                  onClick={() => openSection(s.num)}
                  className={cls(
                    "block w-full border-b border-white/5 px-3 py-2 text-left",
                    view === "section" && selNum === s.num
                      ? "bg-orange-500/10"
                      : "hover:bg-white/5",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-gray-400">
                      {s.num}
                    </span>
                    {TRADE[s.trade] && (
                      <span
                        className={cls(
                          "rounded px-1.5 py-px text-[9.5px] font-semibold",
                          TRADE[s.trade].cls,
                        )}
                      >
                        {TRADE[s.trade].label}
                      </span>
                    )}
                    {s.summary?.at && (
                      <span
                        className="ml-auto text-[10px] text-emerald-400"
                        title="Summarized"
                      >
                        ●
                      </span>
                    )}
                    {busy.has(s.num) && (
                      <span className="ml-auto animate-pulse text-[10px] text-sky-300">
                        …
                      </span>
                    )}
                  </div>
                  <div className="truncate text-[12.5px] text-gray-200">
                    {s.title}
                  </div>
                  <div className="text-[10px] text-gray-600">
                    pages {s.pageFrom}–{s.pageTo}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* main */}
          <main className="flex min-h-0 flex-col">
            <div className="border-b border-white/10 p-3">
              <div className="flex items-center gap-2">
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    if (e.target.value.trim()) setView("search");
                  }}
                  placeholder={`Search the spec — "5/8 type x", "stud gauge", "level 5", "deflection track", "acoustic sealant"…`}
                  className="flex-1 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                />
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  className="rounded-lg border border-gray-700 bg-gray-950 px-2 py-2 text-xs text-gray-200"
                >
                  <option value="ours">Drywall-related sections</option>
                  <option value="all">Whole spec</option>
                </select>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {CHIPS.map(([l, w]) => (
                  <button
                    key={l}
                    onClick={() => {
                      setQ(w);
                      setView("search");
                    }}
                    className="rounded-full bg-white/5 px-2 py-0.5 text-[10.5px] text-gray-400 hover:bg-orange-500/15 hover:text-orange-200"
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div ref={readerRef} className="min-h-0 flex-1 overflow-y-auto p-4">
              {/* search results */}
              {view === "search" && (
                <div className="mx-auto max-w-4xl space-y-2">
                  <div className="text-[11px] text-gray-500">
                    {q.trim()
                      ? `${results.length} article${results.length === 1 ? "" : "s"} match`
                      : "Type to search"}
                  </div>
                  {results.map((r) => {
                    const s = sections.find((x) => x.num === r.chunk.section);
                    return (
                      <button
                        key={r.chunk.key}
                        onClick={() =>
                          openSection(r.chunk.section, r.chunk.key)
                        }
                        className="block w-full rounded-xl border border-white/10 bg-gray-900/70 px-4 py-3 text-left hover:border-orange-500/40"
                      >
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="font-mono text-gray-400">
                            {r.chunk.section}
                          </span>
                          <span className="truncate text-gray-500">
                            {s?.title}
                          </span>
                          {TRADE[s?.trade] && (
                            <span
                              className={cls(
                                "rounded px-1.5 py-px text-[9.5px] font-semibold",
                                TRADE[s.trade].cls,
                              )}
                            >
                              {TRADE[s.trade].label}
                            </span>
                          )}
                          <span className="ml-auto text-gray-600">
                            p.{r.chunk.page}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[13px] font-semibold text-gray-100">
                          {r.chunk.heading}
                        </div>
                        <div className="mt-1 text-[12px] leading-relaxed text-gray-400">
                          <Highlight text={r.snippet} q={q} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* overview */}
              {view === "overview" && spec && (
                <div className="mx-auto max-w-4xl space-y-4">
                  <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5">
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-white">
                          {spec.name}
                        </h2>
                        <div className="text-[11px] text-gray-500">
                          {[
                            spec.meta?.projectNo &&
                              `Project ${spec.meta.projectNo}`,
                            spec.meta?.issuedFor &&
                              `Issued for ${spec.meta.issuedFor}`,
                            spec.meta?.issuedDate,
                            `${spec.pageCount} pages`,
                            `${sections.length} sections`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      {pending > 0 && (
                        <button
                          onClick={summarizeAll}
                          disabled={!!batch}
                          className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-50"
                          title="One short AI call per section, cached — never paid twice"
                        >
                          {batch
                            ? `Summarizing ${batch.done}/${batch.total}…`
                            : `Summarize the ${pending} drywall-related section${pending === 1 ? "" : "s"}`}
                        </button>
                      )}
                      <button
                        onClick={buildOverview}
                        disabled={
                          busy.has("overview") ||
                          !sections.some((s) => s.summary?.at)
                        }
                        className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                      >
                        {busy.has("overview")
                          ? "Writing…"
                          : spec.overview?.at
                            ? "Rebuild overview"
                            : "Build overview"}
                      </button>
                    </div>
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      {core.map((s) => (
                        <button
                          key={s.num}
                          onClick={() => openSection(s.num)}
                          className="flex items-center gap-2 rounded-lg bg-black/25 px-3 py-2 text-left hover:bg-black/40"
                        >
                          <span className="font-mono text-[11px] text-orange-300">
                            {s.num}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-200">
                            {s.title}
                          </span>
                          <span
                            className={cls(
                              "text-[10px]",
                              s.summary?.at
                                ? "text-emerald-400"
                                : "text-gray-600",
                            )}
                          >
                            {s.summary?.at
                              ? "summarized"
                              : busy.has(s.num)
                                ? "…"
                                : "not yet"}
                          </span>
                        </button>
                      ))}
                      {!core.length && (
                        <p className="text-xs text-gray-500">
                          No drywall sections were recognised — use “All” on the
                          left to browse every section.
                        </p>
                      )}
                    </div>
                  </div>

                  {spec.overview?.at && (
                    <div className="rounded-2xl border border-orange-500/20 bg-orange-500/[0.04] p-5">
                      <div className="mb-2 flex items-center gap-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-orange-200">
                          Drywall scope
                        </h3>
                        <span className="text-[10.5px] text-gray-500">
                          {spec.overview.byName
                            ? `by ${spec.overview.byName} · `
                            : ""}
                          {new Date(spec.overview.at).toLocaleDateString()}
                        </span>
                      </div>
                      <MiniMd
                        text={spec.overview.text}
                        className="text-[13px] leading-relaxed text-gray-200"
                      />
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        {[
                          [
                            "Include",
                            spec.overview.inclusions,
                            "text-emerald-300",
                          ],
                          [
                            "Exclude / by others",
                            spec.overview.exclusions,
                            "text-red-300",
                          ],
                        ].map(([h, list, c]) =>
                          list?.length ? (
                            <div key={h}>
                              <div
                                className={cls(
                                  "mb-1 text-[10.5px] font-semibold uppercase tracking-wider",
                                  c,
                                )}
                              >
                                {h}
                              </div>
                              <ul className="space-y-0.5 text-[12.5px] text-gray-300">
                                {list.map((x, i) => (
                                  <li key={i}>• {x}</li>
                                ))}
                              </ul>
                              <button
                                onClick={() =>
                                  navigator.clipboard
                                    ?.writeText(list.join("\n"))
                                    .then(() =>
                                      setNote(
                                        `${h} copied — paste into the estimate's print dialog`,
                                      ),
                                    )
                                }
                                className="mt-1 text-[10.5px] text-gray-500 hover:text-gray-300"
                              >
                                Copy
                              </button>
                            </div>
                          ) : null,
                        )}
                      </div>
                    </div>
                  )}

                  {(spec.overview?.rfis?.length > 0 ||
                    spec.checks?.length > 0) && (
                    <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5">
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-200">
                        Worth asking (RFI)
                      </h3>
                      <div className="space-y-2">
                        {(spec.overview?.rfis || []).map((r, i) => (
                          <RfiIdea
                            key={`o${i}`}
                            idea={r}
                            k={`spec:${spec._id}:ov:${i}`}
                          />
                        ))}
                        {(spec.checks || []).map((r, i) => (
                          <RfiIdea
                            key={`c${i}`}
                            idea={r}
                            k={`spec:${spec._id}:ck:${r.ref}:${i}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* one section */}
              {view === "section" && sel && (
                <div className="mx-auto max-w-4xl space-y-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-orange-300">
                          {sel.num}
                        </span>
                        {TRADE[sel.trade] && (
                          <span
                            className={cls(
                              "rounded px-1.5 py-px text-[10px] font-semibold",
                              TRADE[sel.trade].cls,
                            )}
                          >
                            {TRADE[sel.trade].label}
                          </span>
                        )}
                      </div>
                      <h2 className="text-lg font-semibold text-white">
                        {sel.title}
                      </h2>
                      <div className="text-[11px] text-gray-500">
                        pages {sel.pageFrom}–{sel.pageTo} · {selChunks.length}{" "}
                        articles
                      </div>
                    </div>
                    <button
                      onClick={() => summarize(sel.num, !!sel.summary?.at)}
                      disabled={busy.has(sel.num)}
                      className={cls(
                        "rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50",
                        sel.summary?.at
                          ? "bg-white/5 text-gray-400 hover:bg-white/10"
                          : "bg-orange-500 text-white hover:bg-orange-400",
                      )}
                      title={
                        sel.summary?.at
                          ? "Summarize again (one more AI call)"
                          : `One AI call, ≈${fmtK(Math.min(sel.chars || 0, 26000) / 4)} tokens — saved for everyone`
                      }
                    >
                      {busy.has(sel.num)
                        ? "Summarizing…"
                        : sel.summary?.at
                          ? "Redo summary"
                          : "Summarize"}
                    </button>
                  </div>

                  {sel.summary?.at && (
                    <div className="rounded-2xl border border-orange-500/20 bg-orange-500/[0.04] p-4">
                      <MiniMd
                        text={sel.summary.text}
                        className="text-[13px] leading-relaxed text-gray-200"
                      />
                      {sel.summary.facts?.length > 0 && (
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          {sel.summary.facts.map((f, i) => (
                            <div
                              key={i}
                              className="rounded-lg bg-black/25 px-3 py-2"
                            >
                              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                {f.label}
                              </div>
                              <div className="text-[12.5px] text-gray-100">
                                {f.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {sel.summary.byOthers?.length > 0 && (
                        <div className="mt-3 text-[12px] text-gray-400">
                          <span className="font-semibold text-gray-300">
                            By others / other sections:{" "}
                          </span>
                          {sel.summary.byOthers.join(" · ")}
                        </div>
                      )}
                      {sel.summary.rfis?.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {sel.summary.rfis.map((r, i) => (
                            <RfiIdea
                              key={i}
                              idea={{
                                ...r,
                                section: sel.num,
                                ref: r.ref ? `${sel.num} §${r.ref}` : sel.num,
                              }}
                              k={`spec:${spec._id}:${sel.num}:${i}`}
                            />
                          ))}
                        </div>
                      )}
                      <div className="mt-2 text-[10px] text-gray-600">
                        AI summary
                        {sel.summary.byName ? ` by ${sel.summary.byName}` : ""}{" "}
                        · {new Date(sel.summary.at).toLocaleDateString()} —
                        check the articles below before relying on it
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {selChunks.map((c) => (
                      <details
                        key={c.key}
                        data-key={c.key}
                        open={focusKey ? c.key === focusKey : true}
                        className={cls(
                          "rounded-xl border bg-gray-900/60",
                          c.key === focusKey
                            ? "border-orange-500/50"
                            : "border-white/10",
                        )}
                      >
                        <summary className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[13px] font-semibold text-gray-100">
                          {c.heading}
                          <span className="ml-auto text-[10.5px] font-normal text-gray-600">
                            {c.part ? `${c.part} · ` : ""}p.{c.page}
                          </span>
                        </summary>
                        <pre className="whitespace-pre-wrap border-t border-white/5 px-4 py-3 font-sans text-[12.5px] leading-relaxed text-gray-300">
                          <Highlight text={c.text} q={q} />
                        </pre>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>

          {/* chat */}
          {chatOpen && (
            <aside className="flex min-h-0 flex-col border-l border-white/10 bg-gray-900/40">
              <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
                <div>
                  <div className="text-[13px] font-semibold text-white">
                    Ask the spec
                  </div>
                  <div className="text-[10.5px] text-gray-500">
                    Answers come from the best-matching articles only
                  </div>
                </div>
                {messages.length > 0 && (
                  <button
                    onClick={clearChat}
                    className="ml-auto text-[11px] text-gray-500 hover:text-gray-300"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                {!messages.length && (
                  <div className="space-y-1.5">
                    {STARTERS.map((t) => (
                      <button
                        key={t}
                        disabled={!spec || asking}
                        onClick={() => send(t)}
                        className="block w-full rounded-lg bg-white/5 px-3 py-2 text-left text-[12px] text-gray-300 hover:bg-white/10"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                {messages.map((m, i) =>
                  m.role === "user" ? (
                    <div
                      key={i}
                      className="ml-8 rounded-xl rounded-br-sm bg-orange-500/15 px-3 py-2 text-[12.5px] text-orange-50"
                    >
                      {m.text}
                    </div>
                  ) : (
                    <div
                      key={i}
                      className="mr-4 rounded-xl rounded-bl-sm bg-white/5 px-3 py-2"
                    >
                      <MiniMd
                        text={m.text}
                        className="text-[12.5px] leading-relaxed text-gray-200"
                      />
                      {m.refs?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {m.refs.slice(0, 6).map((r) => (
                            <button
                              key={r.key}
                              onClick={() => openSection(r.section, r.key)}
                              title={r.heading}
                              className="rounded bg-black/30 px-1.5 py-0.5 font-mono text-[10px] text-gray-400 hover:text-orange-200"
                            >
                              {r.section} §{String(r.heading).split(" ")[0]}
                            </button>
                          ))}
                        </div>
                      )}
                      {onAddToRfi && (
                        <button
                          onClick={() =>
                            toRfi(
                              {
                                question: messages[i - 1]?.text || "",
                                why: "Asked in the spec scanner",
                                ref: m.refs?.[0]
                                  ? `${m.refs[0].section} §${String(m.refs[0].heading).split(" ")[0]}`
                                  : "",
                                page: m.refs?.[0]?.page,
                                section: m.refs?.[0]?.section,
                              },
                              `spec:${spec?._id}:chat:${m.at}`,
                            )
                          }
                          disabled={added.has(`spec:${spec?._id}:chat:${m.at}`)}
                          className="mt-1.5 text-[10.5px] text-amber-300/80 hover:text-amber-200 disabled:opacity-40"
                        >
                          {added.has(`spec:${spec?._id}:chat:${m.at}`)
                            ? "✓ Added to RFI"
                            : "+ Ask this in an RFI"}
                        </button>
                      )}
                    </div>
                  ),
                )}
                {asking && (
                  <div className="mr-4 animate-pulse rounded-xl bg-white/5 px-3 py-2 text-[12px] text-gray-500">
                    Reading the spec…
                  </div>
                )}
                <div ref={chatEnd} />
              </div>
              <div className="border-t border-white/10 p-3">
                <div className="flex gap-2">
                  <textarea
                    value={ask}
                    onChange={(e) => setAsk(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    rows={2}
                    disabled={!spec}
                    placeholder="e.g. Is Level 5 required anywhere? What gauge for openings?"
                    className="flex-1 resize-none rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-2 text-[12.5px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    onClick={() => send()}
                    disabled={!ask.trim() || asking || !spec}
                    className="self-end rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                  >
                    Ask
                  </button>
                </div>
                <div className="mt-1 text-[10px] text-gray-600">
                  Enter to send · Shift+Enter for a new line · the last 3
                  exchanges are remembered
                </div>
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
